import { getState } from '@client/state/reducer';
import { advtPerformance } from '@schibsted-nmp/advertising-events';
import {
  ExternalIdEid,
  featureManager,
  getKeywordByKey,
  PersistedPPID
} from '@schibsted-nmp/advertising-shared';

import { getPPID } from './ppid';
import { getBrandCode } from './externalId';
import { getConfigId } from './relevantDigitalConfig';

const features = getState().initialState?.unleash?.features;

// eslint-disable-next-line consistent-return
export function loadTagsWithRelevantDigitalPrebid(params = {}) {
  const isDisabledByFeatureFlag =
    featureManager(features).isDisableRelevantDigitalEnabled();
  if (!isDisabledByFeatureFlag && typeof window !== 'undefined') {
    window.relevantDigital = window.relevantDigital || {};
    window.relevantDigital.cmd = window.relevantDigital.cmd || [];

    const configId = configIdByBrand();
    if (!configId) return window.apntag.loadTags();

    let cancel; // if true => we've timed out and and "emergency-call" to apntag.loadTags() has already been done
    const events: any = {
      relevantCb: { timeout: 1000 }, // Wait up to 1 second for the Relevant script
      auctionCb: { timeout: 2000 } // Wait up to 2 seconds for the auction to complete
    };

    const failsafeOnTimeout = (ev) =>
      setTimeout(() => {
        if (!events[ev].done) {
          console.log(
            `Doing failsafe loadTags() call after waiting too long for '${ev}'`
          );
          cancel = true;

          window.apntag.loadTags();
        }
      }, events[ev].timeout);

    failsafeOnTimeout('relevantCb');

    window.relevantDigital.cmd.push(async () => {
      if (cancel) {
        // We were too slow -  do nothing
        return;
      }
      events.relevantCb.done = true;
      failsafeOnTimeout('auctionCb');

      window.relevantDigital.addPrebidConfig({
        // s2sConfig: { syncEndpoint: null }, // Don't use PBS cookie syncing
        enableTIDs: true,
        enableSendAllBids: false, // Only send key-values for *winning* bids (HB_PB etc)
        userSync: getFormattedUserIdObject(getPPID()),
        ortb2: getOrtb2Data()
      });

      window.relevantDigital.loadPrebid({
        configId,
        manageAdserver: false,
        loadUnknownSlots: true, // Load placements/tags not added in Yield
        noSlotReload: true, // Don't reload placements that has already been loaded
        noGpt: true, // Don't load gpt.js as it's done above
        apntagCalls: {
          showTag: () => {
            /** Skip, we're doing this ourselves */
          },
          loadTags: (...args) => {
            if (!cancel) {
              if (window.apntag && window.apntag.anq) {
                window.apntag.anq.push(() => {
                  if (window.apntag) {
                    events.auctionCb.done = true;
                    advtPerformance.markXandrEvents(
                      `relevant digital loadTags callback(${JSON.stringify(
                        args
                      )})`
                    );
                    window.apntag.loadTags();
                  }
                });
              }
            } // else => We were too slow - do nothing
          }
        },
        ...params
      });
    });
  }
}

// Get config ID by brand and device type
function configIdByBrand() {
  const brand = getState().config?.brand || 'TORI';
  const deviceType = getState().config?.deviceType || 'desktop';

  return getConfigId(brand, deviceType);
}

function getOrtb2Data() {
  const sectionData = getKeywordByKey('no-sno-finn-section');
  const subsectionData = getKeywordByKey('no-sno-finn-subsection');

  if (sectionData || subsectionData) {
    return {
      site: {
        ext: {
          data: {
            'no-sno-finn-section': sectionData,
            'no-sno-finn-subsection': subsectionData
          }
        }
      }
    };
  }
  return undefined;
}

function getFormattedUserIdObject(persistedPPID: PersistedPPID) {
  const eids: ExternalIdEid[] = [];
  const brand = getState().config?.brand;

  if (persistedPPID.ppid1) {
    eids.push({
      source: `SCH${getBrandCode(brand)}-UserHash`,
      uids: [
        {
          id: persistedPPID.ppid1, // PPID1
          atype: 1,
          ext: {
            stype: 'ppuid'
          }
        }
      ]
    });
  }

  if (persistedPPID.ppid2) {
    eids.push({
      source: `SCH${getBrandCode(brand)}-EnvHash`,
      uids: [
        {
          id: persistedPPID.ppid2, // PPID2
          atype: 1,
          ext: {
            stype: 'ppuid'
          }
        }
      ]
    });
  }
  return {
    userIds: [
      {
        name: 'pubProvidedId',
        params: {
          eids
        }
      }
    ]
  };
}
